import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './TeacherSections.css';

const TeacherSectionProfileSettings = () => {
  const { t } = useTranslation('teacher');
  const [activeTab, setActiveTab] = useState('basic-info');
  const [profileData, setProfileData] = useState({
    // Basic Info
    fullName: 'Ms. Sarah Smith',
    username: 'sarah_smith_teacher',
    gender: 'female',
    birthday: '1985-03-15',
    country: 'United States',
    preferredLanguage: 'English',
    profilePicture: null,
    
    // Professional Information
    teacherId: 'TCH-2024-001',
    teachingSubjects: ['English', 'ESL', 'Vocabulary'],
    educationalLevel: 'university',
    bio: 'Experienced English teacher with 8+ years of teaching experience. Specialized in ESL, Business English, and IELTS preparation. Passionate about making learning engaging and effective.',
    experience: 8,
    resumeFile: null,
    teachingLicenseId: 'TL-2024-001',
    
    // Account Settings
    email: 'sarah.smith@email.com',
    emailVerified: true,
    phone: '+1 (555) 123-4567',
    phoneVerified: false,
    twoFactorEnabled: true,
    lastLogin: '2024-03-15T10:30:00Z',
    loginActivity: [
      { device: 'Chrome on Windows', location: 'New York, US', ip: '192.168.1.1', date: '2024-03-15T10:30:00Z', active: true },
      { device: 'Safari on iPhone', location: 'New York, US', ip: '192.168.1.2', date: '2024-03-14T15:20:00Z', active: false },
      { device: 'Firefox on Mac', location: 'Los Angeles, US', ip: '192.168.1.3', date: '2024-03-13T09:15:00Z', active: false }
    ],
    
    // Localization Preferences
    preferredCurrency: 'USD',
    timezone: 'America/New_York',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12-hour',
    
    // Notifications Settings
    notifications: {
      systemAlerts: { inApp: true, email: true, sms: false },
      studentMessages: { inApp: true, email: true, sms: false },
      payoutConfirmations: { inApp: true, email: true, sms: false },
      marketingNewsletters: { inApp: false, email: false, sms: false },
      newCourseEnrollments: { inApp: true, email: true, sms: false },
      liveClassReminders: { inApp: true, email: true, sms: true }
    }
  });

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const tabs = [
    { id: 'basic-info', label: t('profileSettings.tabs.basicInfo'), icon: 'person' },
    { id: 'professional', label: t('profileSettings.tabs.professional'), icon: 'work' },
    { id: 'account', label: t('profileSettings.tabs.account'), icon: 'security' },
    { id: 'localization', label: t('profileSettings.tabs.localization'), icon: 'language' },
    { id: 'notifications', label: t('profileSettings.tabs.notifications'), icon: 'notifications' }
  ];

  const teachingSubjects = [
    'English', 'ESL', 'Vocabulary', 'Grammar', 'Speaking', 'Listening', 
    'Reading', 'Writing', 'Business English', 'IELTS', 'TOEFL', 'Pronunciation'
  ];

  const countries = [
    'United States', 'Canada', 'United Kingdom', 'Australia', 'Germany', 
    'France', 'Spain', 'Italy', 'Japan', 'South Korea', 'China', 'India'
  ];

  const currencies = [
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
    { code: 'JPY', symbol: '¥', name: 'Japanese Yen' }
  ];

  const timezones = [
    'America/New_York', 'America/Los_Angeles', 'Europe/London', 
    'Europe/Paris', 'Asia/Tokyo', 'Asia/Shanghai', 'Australia/Sydney'
  ];

  const handleProfileUpdate = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNotificationUpdate = (category, type, value) => {
    setProfileData(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [category]: {
          ...prev.notifications[category],
          [type]: value
        }
      }
    }));
  };

  const handlePasswordChange = (e) => {
    e.preventDefault();
    if (passwordData.newPassword === passwordData.confirmPassword) {
      console.log('Password changed successfully');
      setShowPasswordModal(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } else {
      alert(t('profileSettings.passwordModal.passwordsDoNotMatch'));
    }
  };

  const handleProfilePictureUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileData(prev => ({
          ...prev,
          profilePicture: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic-info':
        return (
          <div className="teacher-section">
            <div className="teacher-section-header">
              <div className="teacher-section-title">
                <i className="material-icons">person</i>
                {t('profileSettings.basicInfo.title')}
              </div>
            </div>

            <div className="profile-form-grid">
              {/* Profile Picture */}
              <div className="profile-picture-section">
                <h4>{t('profileSettings.basicInfo.profilePicture')}</h4>
                <div className="profile-picture-upload">
                  <div className="profile-picture-preview">
                    {profileData.profilePicture ? (
                      <img src={profileData.profilePicture} alt="Profile" />
                    ) : (
                      <div className="profile-picture-placeholder">
                        <i className="material-icons">person</i>
                      </div>
                    )}
                    <div className="profile-picture-overlay">
                      <i className="material-icons">photo_camera</i>
                      <span>{t('profileSettings.basicInfo.uploadPhoto')}</span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePictureUpload}
                      className="profile-picture-input"
                    />
                  </div>
                  <p className="profile-picture-hint">
                    {t('profileSettings.basicInfo.recommendedImage')}
                  </p>
                </div>
              </div>

              {/* Basic Info Form */}
              <div className="profile-form-section">
                <div className="teacher-form-group">
                  <label htmlFor="fullName">{t('profileSettings.basicInfo.fullName')} *</label>
                  <input
                    type="text"
                    id="fullName"
                    value={profileData.fullName}
                    onChange={(e) => handleProfileUpdate('fullName', e.target.value)}
                    required
                  />
                </div>

                <div className="teacher-form-group">
                  <label htmlFor="username">{t('profileSettings.basicInfo.username')}</label>
                  <input
                    type="text"
                    id="username"
                    value={profileData.username}
                    onChange={(e) => handleProfileUpdate('username', e.target.value)}
                    placeholder={t('profileSettings.basicInfo.usernamePlaceholder')}
                  />
                </div>

                <div className="teacher-form-row">
                  <div className="teacher-form-group">
                    <label htmlFor="gender">{t('profileSettings.basicInfo.gender')}</label>
                    <select
                      id="gender"
                      value={profileData.gender}
                      onChange={(e) => handleProfileUpdate('gender', e.target.value)}
                    >
                      <option value="">{t('profileSettings.basicInfo.selectGender')}</option>
                      <option value="female">{t('profileSettings.basicInfo.female')}</option>
                      <option value="male">{t('profileSettings.basicInfo.male')}</option>
                      <option value="other">{t('profileSettings.basicInfo.other')}</option>
                      <option value="prefer-not-to-say">{t('profileSettings.basicInfo.preferNotToSay')}</option>
                    </select>
                  </div>

                  <div className="teacher-form-group">
                    <label htmlFor="birthday">{t('profileSettings.basicInfo.birthday')}</label>
                    <input
                      type="date"
                      id="birthday"
                      value={profileData.birthday}
                      onChange={(e) => handleProfileUpdate('birthday', e.target.value)}
                    />
                  </div>
                </div>

                <div className="teacher-form-row">
                  <div className="teacher-form-group">
                    <label htmlFor="country">{t('profileSettings.basicInfo.country')} *</label>
                    <select
                      id="country"
                      value={profileData.country}
                      onChange={(e) => handleProfileUpdate('country', e.target.value)}
                      required
                    >
                      <option value="">{t('profileSettings.basicInfo.selectCountry')}</option>
                      {countries.map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                  </div>

                  <div className="teacher-form-group">
                    <label htmlFor="preferredLanguage">{t('profileSettings.basicInfo.preferredLanguage')}</label>
                    <select
                      id="preferredLanguage"
                      value={profileData.preferredLanguage}
                      onChange={(e) => handleProfileUpdate('preferredLanguage', e.target.value)}
                    >
                      <option value="English">English</option>
                      <option value="Spanish">Spanish</option>
                      <option value="French">French</option>
                      <option value="German">German</option>
                      <option value="Arabic">Arabic</option>
                      <option value="Chinese">Chinese</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'professional':
        return (
          <div className="teacher-section">
            <div className="teacher-section-header">
              <div className="teacher-section-title">
                <i className="material-icons">work</i>
                {t('profileSettings.professional.title')}
              </div>
            </div>

            <div className="profile-form-grid">
              <div className="profile-form-section">
                <div className="teacher-form-group">
                  <label>{t('profileSettings.professional.teacherId')}</label>
                  <input
                    type="text"
                    value={profileData.teacherId}
                    disabled
                    className="disabled-input"
                  />
                  <small>{t('profileSettings.professional.autoGenerated')}</small>
                </div>

                <div className="teacher-form-group">
                  <label>{t('profileSettings.professional.teachingSubjects')} *</label>
                  <div className="checkbox-group">
                    {teachingSubjects.map(subject => (
                      <label key={subject} className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={profileData.teachingSubjects.includes(subject)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              handleProfileUpdate('teachingSubjects', [...profileData.teachingSubjects, subject]);
                            } else {
                              handleProfileUpdate('teachingSubjects', profileData.teachingSubjects.filter(s => s !== subject));
                            }
                          }}
                        />
                        <span className="checkmark"></span>
                        {subject}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="teacher-form-group">
                  <label htmlFor="educationalLevel">{t('profileSettings.professional.educationalLevel')}</label>
                  <select
                    id="educationalLevel"
                    value={profileData.educationalLevel}
                    onChange={(e) => handleProfileUpdate('educationalLevel', e.target.value)}
                  >
                    <option value="">{t('profileSettings.professional.selectLevel')}</option>
                    <option value="primary">{t('profileSettings.professional.primarySchool')}</option>
                    <option value="middle">{t('profileSettings.professional.middleSchool')}</option>
                    <option value="high">{t('profileSettings.professional.highSchool')}</option>
                    <option value="university">{t('profileSettings.professional.university')}</option>
                    <option value="adult">{t('profileSettings.professional.adultEducation')}</option>
                  </select>
                </div>

                <div className="teacher-form-group">
                  <label htmlFor="experience">{t('profileSettings.professional.yearsOfExperience')}</label>
                  <input
                    type="number"
                    id="experience"
                    value={profileData.experience}
                    onChange={(e) => handleProfileUpdate('experience', parseInt(e.target.value))}
                    min="0"
                    max="50"
                  />
                </div>

                <div className="teacher-form-group">
                  <label htmlFor="bio">{t('profileSettings.professional.bio')}</label>
                  <textarea
                    id="bio"
                    value={profileData.bio}
                    onChange={(e) => handleProfileUpdate('bio', e.target.value)}
                    rows="4"
                    placeholder={t('profileSettings.professional.bioPlaceholder')}
                  />
                </div>

                <div className="teacher-form-group">
                  <label htmlFor="teachingLicenseId">{t('profileSettings.professional.teachingLicenseId')}</label>
                  <input
                    type="text"
                    id="teachingLicenseId"
                    value={profileData.teachingLicenseId}
                    onChange={(e) => handleProfileUpdate('teachingLicenseId', e.target.value)}
                    placeholder={t('profileSettings.professional.licensePlaceholder')}
                  />
                </div>

                <div className="teacher-form-group">
                  <label>{t('profileSettings.professional.resumeUpload')}</label>
                  <div className="file-upload-area">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setProfileData(prev => ({
                            ...prev,
                            resumeFile: file.name
                          }));
                        }
                      }}
                      className="file-input"
                    />
                    <div className="file-upload-content">
                      <i className="material-icons">upload_file</i>
                      <p>{t('profileSettings.professional.clickToUpload')}</p>
                      <small>{t('profileSettings.professional.fileFormat')}</small>
                    </div>
                  </div>
                  {profileData.resumeFile && (
                    <div className="file-uploaded">
                      <i className="material-icons">description</i>
                      <span>{profileData.resumeFile}</span>
                      <button 
                        className="remove-file-btn"
                        onClick={() => handleProfileUpdate('resumeFile', null)}
                      >
                        <i className="material-icons">close</i>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 'account':
        return (
          <div className="teacher-section">
            <div className="teacher-section-header">
              <div className="teacher-section-title">
                <i className="material-icons">security</i>
                {t('profileSettings.account.title')}
              </div>
            </div>

            <div className="profile-form-grid">
              <div className="profile-form-section">
                <div className="teacher-form-group">
                  <label htmlFor="email">{t('profileSettings.account.emailAddress')}</label>
                  <div className="input-with-status">
                    <input
                      type="email"
                      id="email"
                      value={profileData.email}
                      onChange={(e) => handleProfileUpdate('email', e.target.value)}
                      required
                    />
                    <span className={`status-badge ${profileData.emailVerified ? 'success' : 'warning'}`}>
                      {profileData.emailVerified ? t('profileSettings.account.verified') : t('profileSettings.account.unverified')}
                    </span>
                  </div>
                  {!profileData.emailVerified && (
                    <button className="teacher-btn secondary">{t('profileSettings.account.verifyEmail')}</button>
                  )}
                </div>

                <div className="teacher-form-group">
                  <label htmlFor="phone">{t('profileSettings.account.phoneNumber')}</label>
                  <div className="input-with-status">
                    <input
                      type="tel"
                      id="phone"
                      value={profileData.phone}
                      onChange={(e) => handleProfileUpdate('phone', e.target.value)}
                    />
                    <span className={`status-badge ${profileData.phoneVerified ? 'success' : 'warning'}`}>
                      {profileData.phoneVerified ? t('profileSettings.account.verified') : t('profileSettings.account.unverified')}
                    </span>
                  </div>
                  {!profileData.phoneVerified && (
                    <button className="teacher-btn secondary">{t('profileSettings.account.verifyPhone')}</button>
                  )}
                </div>

                <div className="teacher-form-group">
                  <label>{t('profileSettings.account.password')}</label>
                  <button 
                    className="teacher-btn secondary"
                    onClick={() => setShowPasswordModal(true)}
                  >
                    <i className="material-icons">lock</i>
                    {t('profileSettings.account.changePassword')}
                  </button>
                </div>

                <div className="teacher-form-group">
                  <label>{t('profileSettings.account.twoFactorAuth')}</label>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      id="twoFactor"
                      checked={profileData.twoFactorEnabled}
                      onChange={(e) => handleProfileUpdate('twoFactorEnabled', e.target.checked)}
                    />
                    <label htmlFor="twoFactor" className="toggle-label"></label>
                    <span className="toggle-text">
                      {profileData.twoFactorEnabled ? t('profileSettings.account.enabled') : t('profileSettings.account.disabled')}
                    </span>
                  </div>
                </div>

                <div className="teacher-form-group">
                  <label>{t('profileSettings.account.loginActivity')}</label>
                  <div className="login-activity-list">
                    {profileData.loginActivity.map((activity, index) => (
                      <div key={index} className={`login-activity-item ${activity.active ? 'active' : ''}`}>
                        <div className="activity-info">
                          <div className="activity-device">
                            <i className="material-icons">computer</i>
                            <span>{activity.device}</span>
                          </div>
                          <div className="activity-details">
                            <span className="activity-location">{activity.location}</span>
                            <span className="activity-ip">{activity.ip}</span>
                            <span className="activity-date">
                              {new Date(activity.date).toLocaleString()}
                            </span>
                          </div>
                        </div>
                        {activity.active && (
                          <span className="current-session">{t('profileSettings.account.currentSession')}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="teacher-form-group">
                  <label>{t('profileSettings.account.accountActions')}</label>
                  <div className="account-actions">
                    <button className="teacher-btn danger" onClick={() => setShowDeleteModal(true)}>
                      <i className="material-icons">delete_forever</i>
                      {t('profileSettings.account.deleteAccount')}
                    </button>
                    <button className="teacher-btn warning">
                      <i className="material-icons">pause_circle</i>
                      {t('profileSettings.account.deactivateAccount')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'localization':
        return (
          <div className="teacher-section">
            <div className="teacher-section-header">
              <div className="teacher-section-title">
                <i className="material-icons">language</i>
                {t('profileSettings.localization.title')}
              </div>
            </div>

            <div className="profile-form-grid">
              <div className="profile-form-section">
                <div className="teacher-form-group">
                  <label htmlFor="preferredCurrency">{t('profileSettings.localization.preferredCurrency')}</label>
                  <select
                    id="preferredCurrency"
                    value={profileData.preferredCurrency}
                    onChange={(e) => handleProfileUpdate('preferredCurrency', e.target.value)}
                  >
                    {currencies.map(currency => (
                      <option key={currency.code} value={currency.code}>
                        {currency.symbol} {currency.name} ({currency.code})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="teacher-form-group">
                  <label htmlFor="timezone">{t('profileSettings.localization.timeZone')}</label>
                  <select
                    id="timezone"
                    value={profileData.timezone}
                    onChange={(e) => handleProfileUpdate('timezone', e.target.value)}
                  >
                    {timezones.map(timezone => (
                      <option key={timezone} value={timezone}>
                        {timezone.replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="teacher-form-row">
                  <div className="teacher-form-group">
                    <label htmlFor="dateFormat">{t('profileSettings.localization.dateFormat')}</label>
                    <select
                      id="dateFormat"
                      value={profileData.dateFormat}
                      onChange={(e) => handleProfileUpdate('dateFormat', e.target.value)}
                    >
                      <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                      <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    </select>
                  </div>

                  <div className="teacher-form-group">
                    <label htmlFor="timeFormat">{t('profileSettings.localization.timeFormat')}</label>
                    <select
                      id="timeFormat"
                      value={profileData.timeFormat}
                      onChange={(e) => handleProfileUpdate('timeFormat', e.target.value)}
                    >
                      <option value="12-hour">{t('profileSettings.localization.twelveHour')}</option>
                      <option value="24-hour">{t('profileSettings.localization.twentyFourHour')}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'notifications':
        return (
          <div className="teacher-section">
            <div className="teacher-section-header">
              <div className="teacher-section-title">
                <i className="material-icons">notifications</i>
                {t('profileSettings.notifications.title')}
              </div>
            </div>

            <div className="profile-form-grid">
              <div className="profile-form-section">
                <div className="notification-settings">
                  {Object.entries(profileData.notifications).map(([category, settings]) => (
                    <div key={category} className="notification-category">
                      <h4 className="notification-category-title">
                        {t(`profileSettings.notifications.${category}`, { defaultValue: category.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()) })}
                      </h4>
                      <div className="notification-options">
                        <label className="notification-option">
                          <input
                            type="checkbox"
                            checked={settings.inApp}
                            onChange={(e) => handleNotificationUpdate(category, 'inApp', e.target.checked)}
                          />
                          <span className="checkmark"></span>
                          <span className="option-label">{t('profileSettings.notifications.inApp')}</span>
                        </label>
                        <label className="notification-option">
                          <input
                            type="checkbox"
                            checked={settings.email}
                            onChange={(e) => handleNotificationUpdate(category, 'email', e.target.checked)}
                          />
                          <span className="checkmark"></span>
                          <span className="option-label">{t('profileSettings.notifications.email')}</span>
                        </label>
                        <label className="notification-option">
                          <input
                            type="checkbox"
                            checked={settings.sms}
                            onChange={(e) => handleNotificationUpdate(category, 'sms', e.target.checked)}
                          />
                          <span className="checkmark"></span>
                          <span className="option-label">{t('profileSettings.notifications.sms')}</span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="teacher-main-content">
      <div className="teacher-content-header">
        <h1 className="teacher-content-title">{t('profileSettings.title')}</h1>
        <p className="teacher-content-subtitle">{t('profileSettings.subtitle')}</p>
      </div>

      {/* Tabs Navigation */}
      <div className="teacher-tabs-container">
        <div className="teacher-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`teacher-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <i className="material-icons">{tab.icon}</i>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="teacher-tab-content">
        {renderTabContent()}
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="teacher-modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="teacher-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="teacher-modal-close" onClick={() => setShowPasswordModal(false)}>×</button>
            
            <h2>{t('profileSettings.passwordModal.title')}</h2>
            
            <form className="teacher-form" onSubmit={handlePasswordChange}>
              <div className="teacher-form-group">
                <label htmlFor="currentPassword">{t('profileSettings.passwordModal.currentPassword')}</label>
                <input
                  type="password"
                  id="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                  required
                />
              </div>

              <div className="teacher-form-group">
                <label htmlFor="newPassword">{t('profileSettings.passwordModal.newPassword')}</label>
                <input
                  type="password"
                  id="newPassword"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                  required
                />
              </div>

              <div className="teacher-form-group">
                <label htmlFor="confirmPassword">{t('profileSettings.passwordModal.confirmPassword')}</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  required
                />
              </div>

              <div className="teacher-form-actions">
                <button type="submit" className="teacher-btn primary">
                  <i className="material-icons">lock</i>
                  {t('profileSettings.passwordModal.changePassword')}
                </button>
                <button 
                  type="button" 
                  className="teacher-btn secondary"
                  onClick={() => setShowPasswordModal(false)}
                >
                  {t('profileSettings.passwordModal.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="teacher-modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="teacher-modal-content danger" onClick={(e) => e.stopPropagation()}>
            <button className="teacher-modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
            
            <div className="delete-account-warning">
              <i className="material-icons">warning</i>
              <h2>{t('profileSettings.deleteAccountModal.title')}</h2>
              <p>{t('profileSettings.deleteAccountModal.warning')}</p>
              
              <div className="delete-account-actions">
                <button className="teacher-btn danger">
                  <i className="material-icons">delete_forever</i>
                  {t('profileSettings.deleteAccountModal.permanentlyDelete')}
                </button>
                <button 
                  className="teacher-btn secondary"
                  onClick={() => setShowDeleteModal(false)}
                >
                  {t('profileSettings.deleteAccountModal.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherSectionProfileSettings; 